<template>
  <article id="lotto-wrapper" class="contents-wrapper">
    <div id="lotto-result-check">
      <!-- 페이지 헤더 -->
      <div class="rv-head">
        <h1 class="rv-title">당첨 결과</h1>
        <p class="rv-sub">회차별 공식 당첨번호·당첨금을 확인하고, 내 번호가 당첨됐는지 판정해 보세요.</p>
      </div>

      <!-- 공식 당첨 결과 브라우저 -->
      <div class="section-card result-browser">
        <div class="result-wrapper">
          <div class="lotto-result-wrapper">
            <div class="lotto-result-container">
              <button @click="prevDrw()" class="arrow-button prev" aria-label="이전 회차"></button>
              <LottoResult :lottoResult="result">
                <template #drwNo>
                  <div class="drw-dropdown-wrapper">
                    <span ref="drwNoText" class="drw-no-text" @click.stop="toggleDropdown">
                      {{ result.drwNo }} 회차
                    </span>
                    <div v-if="showDropdown" class="drw-dropdown" :style="dropdownStyle" @click.stop>
                      <div
                        v-for="no in drwNoList"
                        :key="no"
                        class="drw-dropdown-item"
                        :class="{ active: no === result.drwNo }"
                        @click="selectDrwNo(no)"
                      >
                        <span class="drw-item-no">{{ no }} 회차</span>
                      </div>
                    </div>
                  </div>
                </template>
              </LottoResult>
              <button @click="nextDrw()" class="arrow-button next" aria-label="다음 회차"></button>
            </div>
          </div>

          <div class="text-area">
            <div class="prize-table-wrapper" v-if="result.prizes && result.prizes.length > 0">
              <table class="prize-table">
                <thead>
                  <tr>
                    <th>등수</th>
                    <th>당첨자 수</th>
                    <th>1인당 당첨금</th>
                    <th>총 당첨금</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="prize in result.prizes" :key="prize.rank" :class="'prize-rank-' + prize.rank">
                    <td class="rank-cell">
                      <span class="rank-cell-inner">
                        {{ prize.rank }}등
                        <span class="rank-help" :data-tip="rankCondMap[prize.rank]" tabindex="0">?</span>
                      </span>
                    </td>
                    <td>{{ getMoneyFormat(prize.winCount) }}명</td>
                    <td class="amount-cell">
                      <span class="amount-summary">{{ getShortenMoney(prize.winAmount) }}</span>
                      <span class="amount-detail">{{ getMoneyFormat(prize.winAmount) }}원</span>
                    </td>
                    <td class="amount-cell">
                      <span class="amount-summary">{{ getShortenMoney(prize.totalAmount) }}</span>
                      <span class="amount-detail">{{ getMoneyFormat(prize.totalAmount) }}원</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="win-type-info" v-if="hasWinType">
              <span class="win-type-label">1등 구매유형</span>
              <span class="win-type-item" v-if="result.winType1 > 0">자동 {{ result.winType1 }}</span>
              <span class="win-type-item" v-if="result.winType2 > 0">수동 {{ result.winType2 }}</span>
              <span class="win-type-item" v-if="result.winType3 > 0">반자동 {{ result.winType3 }}</span>
              <span class="win-type-item unknown" v-if="result.winType0 > 0">미확인 {{ result.winType0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 내 번호 당첨 확인 -->
      <div class="section-card">
        <div class="section-title">내 번호 당첨 확인</div>
        <p class="section-desc">위에서 선택한 <b>{{ result.drwNo }}회차</b> 기준으로 내 번호를 판정합니다.</p>
        <div class="my-numbers-input">
          <input
            v-for="i in 6"
            :key="'mi-' + i"
            type="number"
            v-model.number="myNumbers[i - 1]"
            class="number-input"
            min="1"
            max="45"
          />
        </div>
        <button class="check-btn" :disabled="!isValidNumbers || result.drwNo === 0" @click="checkMyNumbers">
          판정하기
        </button>

        <div v-if="checkResult !== null" class="check-result" :class="'rank-result-' + checkResult.rank">
          <div class="check-rank">
            {{ checkResult.rank <= 5 ? checkResult.rank + '등 당첨!' : '아쉽게도 낙첨' }}
          </div>
          <div class="check-matched">
            일치 번호: {{ checkResult.matched }}개
            <span v-if="checkResult.hasBonus"> + 보너스</span>
          </div>
          <div v-if="checkResult.prize > 0" class="check-prize">
            예상 당첨금: {{ formatKRW(checkResult.prize) }}
          </div>
          <div class="check-numbers">
            <div
              v-for="(num, i) in myNumbers"
              :key="'cn-' + i"
              class="check-ball"
              :class="[getColorClass(num), { matched: isMatched(num), bonus: isBonusMatch(num) }]"
            >
              {{ num }}
            </div>
          </div>
        </div>
      </div>

      <!-- 브리저 당첨 명예의 전당 (2열) -->
      <div class="win-lists-row">
      <!-- 브리저 최근 당첨 -->
      <div class="section-card">
        <div class="section-title">브리저 최근 당첨</div>
        <p class="section-desc">브리저에서 만들어 구매한 번호 중 당첨된 내역이에요.</p>
        <div v-if="winRecent.length > 0" class="win-hist-list">
          <div v-for="(g, idx) in winRecent" :key="'wr-' + idx" class="win-hist-item">
            <span class="wh-rank" :class="'rank-' + g.rank">{{ g.rank }}등</span>
            <div class="wh-balls">
              <div v-for="(num, i) in gameNumbers(g)" :key="'wrb-' + idx + '-' + i" class="wh-ball" :class="getColorClass(num)">{{ num }}</div>
            </div>
            <span class="wh-meta">{{ g.drwNo }}회 · {{ g.nickname || '익명' }}</span>
          </div>
        </div>
        <div v-else class="wh-empty">아직 당첨 내역이 없어요.</div>
        <div class="pager" v-if="winRecentTotalPages > 1">
          <button class="pager-btn" :disabled="winRecentPage <= 1" @click="fetchWinRecent(winRecentPage - 1)">이전</button>
          <span class="pager-info">{{ winRecentPage }} / {{ winRecentTotalPages }}</span>
          <button class="pager-btn" :disabled="winRecentPage >= winRecentTotalPages" @click="fetchWinRecent(winRecentPage + 1)">다음</button>
        </div>
      </div>

      <!-- 역대 최고 등수 -->
      <div class="section-card">
        <div class="section-title">역대 최고 등수</div>
        <p class="section-desc">브리저 이용자들이 기록한 역대 최고 순위예요.</p>
        <div v-if="bestPageItems.length > 0" class="win-hist-list">
          <div v-for="(g, idx) in bestPageItems" :key="'bp-' + idx" class="win-hist-item">
            <span class="wh-rank" :class="'rank-' + g.rank">{{ g.rank }}등</span>
            <div class="wh-balls">
              <div v-for="(num, i) in gameNumbers(g)" :key="'bpb-' + idx + '-' + i" class="wh-ball" :class="getColorClass(num)">{{ num }}</div>
            </div>
            <span class="wh-meta">{{ g.drwNo }}회 · {{ g.nickname || '익명' }}</span>
          </div>
        </div>
        <div v-else class="wh-empty">아직 기록이 없어요.</div>
        <div class="pager" v-if="bestTotalPages > 1">
          <button class="pager-btn" :disabled="bestPage <= 1" @click="bestPage--">이전</button>
          <span class="pager-info">{{ bestPage }} / {{ bestTotalPages }}</span>
          <button class="pager-btn" :disabled="bestPage >= bestTotalPages" @click="bestPage++">다음</button>
        </div>
      </div>
      </div>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent, nextTick } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { LottoResultResponse, LottoGameBuyResponse } from "../class";
import LottoResult from "../components/LottoResult.vue";
import * as lottoUtil from "../utils/LottoUtils";

interface CheckResultInfo {
  rank: number;
  matched: number;
  hasBonus: boolean;
  prize: number;
}

export default defineComponent({
  name: "LottoResultView",
  components: { LottoResult },
  mixins: [UtilMixin],
  data() {
    return {
      result: new LottoResultResponse(),
      lastDrwNo: 0,
      showDropdown: false,
      dropdownStyle: {} as Record<string, string>,
      myNumbers: [0, 0, 0, 0, 0, 0] as number[],
      checkResult: null as CheckResultInfo | null,
      // 브리저 최근 당첨 (서버 페이징)
      winRecent: [] as LottoGameBuyResponse[],
      winRecentPage: 1,
      winRecentTotalPages: 1,
      // 역대 최고 등수 (클라 정렬 후 페이징)
      bestAll: [] as LottoGameBuyResponse[],
      bestPage: 1,
      bestPageSize: 10,
      rankCondMap: {
        1: "6개 번호 모두 일치 · 총 당첨금의 75%",
        2: "5개 + 보너스 번호 일치 · 총 당첨금의 12.5%",
        3: "5개 번호 일치 · 총 당첨금의 12.5%",
        4: "4개 번호 일치 · 고정 50,000원",
        5: "3개 번호 일치 · 고정 5,000원",
      } as Record<number, string>,
    };
  },
  computed: {
    winNumbers(): number[] {
      const r = this.result;
      return [r.drwtNo1, r.drwtNo2, r.drwtNo3, r.drwtNo4, r.drwtNo5, r.drwtNo6];
    },
    isValidNumbers(): boolean {
      return this.myNumbers.every((n) => n >= 1 && n <= 45) && new Set(this.myNumbers).size === 6;
    },
    drwNoList(): number[] {
      const list = [];
      for (let i = this.lastDrwNo; i >= 1; i--) list.push(i);
      return list;
    },
    hasWinType(): boolean {
      const r = this.result;
      return r.winType0 + r.winType1 + r.winType2 + r.winType3 > 0;
    },
    bestTotalPages(): number {
      return Math.max(1, Math.ceil(this.bestAll.length / this.bestPageSize));
    },
    bestPageItems(): LottoGameBuyResponse[] {
      const start = (this.bestPage - 1) * this.bestPageSize;
      return this.bestAll.slice(start, start + this.bestPageSize);
    },
  },
  methods: {
    getColorClass(number: number): string {
      if (number >= 1 && number <= 9) return "bg-yellow-400";
      if (number >= 10 && number <= 19) return "bg-blue-400";
      if (number >= 20 && number <= 29) return "bg-red-400";
      if (number >= 30 && number <= 39) return "bg-gray-400";
      if (number >= 40 && number <= 45) return "bg-green-400";
      return "";
    },
    formatKRW(amount: number): string {
      if (amount >= 100000000) return (amount / 100000000).toFixed(1) + "억원";
      if (amount >= 10000) return (amount / 10000).toFixed(0) + "만원";
      return amount.toLocaleString() + "원";
    },
    getShortenMoney(amount: number): string {
      if (amount >= 100000000) {
        const eok = Math.floor(amount / 100000000);
        const man = Math.floor((amount % 100000000) / 10000);
        return man > 0 ? `${eok}억 ${this.getMoneyFormat(man)}만원` : `${eok}억원`;
      } else if (amount >= 10000) {
        return `${this.getMoneyFormat(Math.floor(amount / 10000))}만원`;
      }
      return `${this.getMoneyFormat(amount)}원`;
    },
    async getLast() {
      const res = await this.getApi().lotto().getLastLottoResult();
      this.lastDrwNo = res.drwNo;
      // 홈에서 회차를 지정해 넘어온 경우 해당 회차로 진입
      const q = this.$route.query.drwNo;
      const drwNo = q ? parseInt(String(q), 10) : 0;
      if (drwNo > 0 && drwNo <= res.drwNo) {
        await this.loadDrw(drwNo);
      } else {
        this.result = res;
      }
    },
    async loadDrw(drwNo: number) {
      this.checkResult = null;
      const res = await this.getApi().lotto().getLottoResult(drwNo, false);
      if (res && res.drwNo > 0) this.result = res;
    },
    async prevDrw() {
      if (this.result.drwNo <= 1) return;
      await this.loadDrw(this.result.drwNo - 1);
    },
    async nextDrw() {
      if (this.result.drwNo >= this.lastDrwNo) return;
      await this.loadDrw(this.result.drwNo + 1);
    },
    async toggleDropdown() {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        await nextTick();
        this.updateDropdownPosition();
      }
    },
    updateDropdownPosition() {
      const el = this.$refs.drwNoText as HTMLElement;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      this.dropdownStyle = {
        top: rect.bottom + 4 + "px",
        left: rect.left + rect.width / 2 + "px",
        transform: "translateX(-50%)",
      };
    },
    async selectDrwNo(drwNo: number) {
      this.showDropdown = false;
      if (drwNo === this.result.drwNo) return;
      await this.loadDrw(drwNo);
    },
    closeDropdown() {
      this.showDropdown = false;
    },
    checkMyNumbers() {
      if (!this.isValidNumbers || this.result.drwNo === 0) return;
      const sorted = [...this.myNumbers].sort((a, b) => a - b);
      const matched = sorted.filter((n) => this.winNumbers.includes(n)).length;
      const hasBonus = sorted.includes(this.result.bnusNo);
      let rank = 6;
      if (matched === 6) rank = 1;
      else if (matched === 5 && hasBonus) rank = 2;
      else if (matched === 5) rank = 3;
      else if (matched === 4) rank = 4;
      else if (matched === 3) rank = 5;
      let prize = 0;
      if (rank === 1) prize = this.result.firstWinamnt;
      else if (rank === 4) prize = 50000;
      else if (rank === 5) prize = 5000;
      this.checkResult = { rank, matched, hasBonus, prize };
    },
    isMatched(num: number): boolean {
      return this.winNumbers.includes(num);
    },
    isBonusMatch(num: number): boolean {
      return num === this.result.bnusNo && !this.winNumbers.includes(num);
    },
    gameNumbers(game: LottoGameBuyResponse): number[] {
      return [game.drwtNo1, game.drwtNo2, game.drwtNo3, game.drwtNo4, game.drwtNo5, game.drwtNo6];
    },
    async fetchWinRecent(page: number) {
      if (page < 1) return;
      try {
        const res = await this.getApi().lotto().getWinGameList(page, 10);
        if (res && Array.isArray(res.content)) {
          this.winRecent = res.content.map((item: any) => {
            const g = new LottoGameBuyResponse();
            g.init(item);
            return g;
          });
          this.winRecentPage = page;
          this.winRecentTotalPages = res.totalPages || 1;
        }
      } catch (e) {
        console.error("fetchWinRecent error:", e);
      }
    },
    async fetchBest() {
      try {
        const res = await this.getApi().lotto().getWinGameList(1, 500);
        if (res && Array.isArray(res.content)) {
          const games = res.content.map((item: any) => {
            const g = new LottoGameBuyResponse();
            g.init(item);
            return g;
          });
          this.bestAll = games.sort((a: LottoGameBuyResponse, b: LottoGameBuyResponse) => (a.rank - b.rank) || (b.drwNo - a.drwNo));
        }
      } catch (e) {
        console.error("fetchBest error:", e);
      }
    },
  },
  created() {
    window.scrollTo(0, 0);
    this.getLast();
    this.fetchWinRecent(1);
    this.fetchBest();
    document.addEventListener("click", this.closeDropdown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdown);
  },
});
</script>

<style lang="scss" scoped></style>
